import store from 'app/store';

import { selectAuthRegion, selectAuthToken } from 'features/auth/authSelectors';

import checkStatus from 'utils/api/checkStatus';
import errorParser from 'utils/api/errorParser';
import responseParser from 'utils/api/responseParser';

import { getChatbotHostForRegion, getHostForRegion } from 'constants/api';
import { SHARED_HOST } from 'constants/endpoints';
import { Region } from 'constants/enums';

interface APIConfig {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: HeadersInit;
  body?: string | FormData;
  includeContentType?: boolean;
}

const getContentTypeHeader = (config: APIConfig): Record<string, string> => {
  if (config.includeContentType === false || config.body instanceof FormData) {
    return {};
  }
  return { 'Content-Type': 'application/json' };
};

const getAuthHeader = (token: string | null): Record<string, string> => {
  if (!token) return {};
  return { Authorization: token };
};

const fetchApi = (
  path: string,
  config: APIConfig,
  host: string,
  isFile = false
): Promise<any> => {
  const token = selectAuthToken(store.getState());

  const headers: HeadersInit = {
    ...getContentTypeHeader(config),
    ...getAuthHeader(token),
    ...config.headers
  };

  const fullConfig: RequestInit = {
    ...config,
    headers
  };

  const url = `${host}${path}`;

  return fetch(url, fullConfig)
    .then(checkStatus)
    .then((data) => responseParser(data, isFile))
    .catch(errorParser);
};

const createRegionBasedResolver =
  (getHost: (region: Region | null | undefined) => string) => () => {
    const state = store.getState();
    const token = selectAuthToken(state);
    const region = selectAuthRegion(state);

    if (!token) {
      return getHost(Region.US);
    }

    return getHost(region);
  };

export const resolveMainApiHost = createRegionBasedResolver(getHostForRegion);

export const resolveChatbotApiHost = createRegionBasedResolver(
  getChatbotHostForRegion
);

export const createFetcher = (resolveHost: () => string) => ({
  get: (path: string, headers?: HeadersInit): Promise<any> =>
    fetchApi(path, { method: 'GET', headers }, resolveHost()),

  getFile: (path: string): Promise<Blob> =>
    fetchApi(
      path,
      { method: 'GET', includeContentType: false },
      resolveHost(),
      true
    ),

  post: (path: string, body: object, headers?: HeadersInit): Promise<any> =>
    fetchApi(
      path,
      { method: 'POST', body: JSON.stringify(body), headers },
      resolveHost()
    ),

  postFormData: (
    path: string,
    body: FormData,
    headers?: HeadersInit
  ): Promise<any> =>
    fetchApi(path, { method: 'POST', body, headers }, resolveHost()),

  put: (path: string, body: object, headers?: HeadersInit): Promise<any> =>
    fetchApi(
      path,
      { method: 'PUT', body: JSON.stringify(body), headers },
      resolveHost()
    ),

  putFormData: (
    path: string,
    body: FormData,
    headers?: HeadersInit
  ): Promise<any> =>
    fetchApi(path, { method: 'PUT', body, headers }, resolveHost()),

  patch: (path: string, body?: object, headers?: HeadersInit): Promise<any> =>
    fetchApi(
      path,
      {
        method: 'PATCH',
        body: body ? JSON.stringify(body) : body,
        headers
      },
      resolveHost()
    ),

  delete: (path: string, headers?: HeadersInit): Promise<any> =>
    fetchApi(path, { method: 'DELETE', headers }, resolveHost())
});

export const api = createFetcher(resolveMainApiHost);
export const chatbotApi = createFetcher(resolveChatbotApiHost);
export const sharedApi = createFetcher(() => SHARED_HOST);

// Backward-compatible named exports bound to default HOST
export const get = (path: string, headers?: HeadersInit): Promise<any> =>
  api.get(path, headers);
export const getFile = (path: string): Promise<Blob> => api.getFile(path);
export const post = (
  path: string,
  body: object,
  headers?: HeadersInit
): Promise<any> => api.post(path, body, headers);
export const postFormData = (
  path: string,
  body: FormData,
  headers?: HeadersInit
): Promise<any> => api.postFormData(path, body, headers);
export const put = (
  path: string,
  body: object,
  headers?: HeadersInit
): Promise<any> => api.put(path, body, headers);
export const putFormData = (
  path: string,
  body: FormData,
  headers?: HeadersInit
): Promise<any> => api.putFormData(path, body, headers);
export const patch = (
  path: string,
  body?: object,
  headers?: HeadersInit
): Promise<any> => api.patch(path, body, headers);
export const del = (path: string, headers?: HeadersInit): Promise<any> =>
  api.delete(path, headers);

export default api;
